if (!customElements.get('impressiko-pincode-checker')) {
    customElements.define('impressiko-pincode-checker', class ImpressikoPincodeChecker extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.form = this.querySelector('[data-trpc-form]');
        this.input = this.querySelector('[data-trpc-input]');
        this.button = this.querySelector('[data-trpc-button]');
        this.buttonText = this.querySelector('[data-trpc-button-text]');
        this.result = this.querySelector('[data-trpc-result]');
        this.resultMessage = this.querySelector('[data-trpc-result-message]');
        this.zoneBadge = this.querySelector('[data-trpc-zone]');
        this.config = this.readJSON('[data-trpc-config]', {});
        this.cache = new Map();
        this.requestId = 0;
        this.zones = (this.config.zones || []).map((zone) => ({ ...zone, values: this.parseList(zone.matchValues) })).sort((a, b) => Number(a.priority) - Number(b.priority));
        this.onSubmit = (event) => { event.preventDefault(); this.check(); };
        this.onInput = () => {
          this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
          this.requestId += 1;
          this.setLoading(false);
          this.hideResult();
        };
        this.form?.addEventListener('submit', this.onSubmit);
        this.input?.addEventListener('input', this.onInput);
      }

      disconnectedCallback() {
        this.requestId += 1;
        this.form?.removeEventListener('submit', this.onSubmit);
        this.input?.removeEventListener('input', this.onInput);
        this.initialized = false;
      }

      readJSON(selector, fallback) {
        try { return JSON.parse(this.querySelector(selector)?.textContent || ''); }
        catch (_error) { return fallback; }
      }

      normalize(value) {
        return String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
      }

      parseList(value) {
        return String(value || '').split(/[\n,;]+/).map((item) => this.normalize(item)).filter(Boolean);
      }

      escapeHTML(value) {
        return String(value ?? '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char]);
      }

      setLoading(loading) {
        this.button?.classList.toggle('is-loading', loading);
        if (this.button) {
          this.button.disabled = loading;
          this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
        }
        if (this.buttonText) this.buttonText.textContent = loading ? this.config.checkingLabel : this.config.buttonLabel;
      }

      hideResult() {
        if (!this.result) return;
        this.result.hidden = true;
        this.result.className = 'trpc-result';
        if (this.resultMessage) this.resultMessage.textContent = '';
        if (this.zoneBadge) this.zoneBadge.hidden = true;
      }

      showResult(type, message, zoneLabel = '') {
        if (!this.result || !this.resultMessage) return;
        this.result.hidden = false;
        this.result.className = `trpc-result is-${type}${this.config.animateResult ? ' is-animated' : ''}`;
        this.resultMessage.innerHTML = message;
        if (this.zoneBadge) {
          this.zoneBadge.textContent = zoneLabel;
          this.zoneBadge.hidden = !(this.config.showZone && zoneLabel && type === 'success');
        }
      }

      endpoint(pincode) {
        const template = String(this.config.apiUrl || '');
        if (template.includes('[pincode]')) return template.replaceAll('[pincode]', encodeURIComponent(pincode));
        return `${template.replace(/\/$/, '')}/${encodeURIComponent(pincode)}`;
      }

      async validatePincode(pincode) {
        if (this.cache.has(pincode)) return this.cache.get(pincode);
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), Number(this.config.apiTimeout) || 8000);
        try {
          const response = await fetch(this.endpoint(pincode), { method: 'GET', mode: 'cors', credentials: 'omit', signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`Postal lookup returned ${response.status}`);
          const data = await response.json();
          const first = Array.isArray(data) ? data[0] : null;
          const offices = first && Array.isArray(first.PostOffice) ? first.PostOffice : [];
          const success = first && this.normalize(first.Status) === 'success' && offices.length > 0;
          const deliveryOffices = offices.filter((office) => this.normalize(office.DeliveryStatus || office.Delivery) === 'delivery');
          const valid = Boolean(success && (!this.config.requireDeliveryOffice || deliveryOffices.length > 0));
          const result = { valid, offices: valid ? (deliveryOffices.length ? deliveryOffices : offices) : [], apiError: false };
          this.cache.set(pincode, result);
          return result;
        } catch (error) {
          return { valid: false, offices: [], apiError: true, error };
        } finally {
          window.clearTimeout(timeout);
        }
      }

      matchesZone(zone, pincode, offices) {
        const type = zone.matchType;
        if (type === 'fallback') return true;
        if (type === 'exact_pincodes') return zone.values.includes(this.normalize(pincode));
        if (type === 'pincode_prefixes') return zone.values.some((prefix) => pincode.startsWith(prefix));
        const states = offices.map((office) => this.normalize(office.State));
        if (type === 'state_exact') return zone.values.some((value) => states.includes(value));
        if (type === 'location_keywords') {
          // Keep metro matching to the office/locality, block, district and state.
          // Postal region/division names can cover nearby non-metro cities and would
          // otherwise produce false Zone C matches.
          const locationText = offices.map((office) => [office.Name, office.Block, office.District, office.State].map((value) => this.normalize(value)).join(' ')).join(' | ');
          return zone.values.some((value) => locationText.includes(value));
        }
        return false;
      }

      findZone(pincode, offices) {
        const specific = this.zones.find((zone) => zone.matchType !== 'fallback' && this.matchesZone(zone, pincode, offices));
        return specific || this.zones.find((zone) => zone.matchType === 'fallback') || { label: 'Standard', days: Number(this.config.fallbackDays) || 7 };
      }

      indiaNow() {
        const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
        const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
        return { date: new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day), 12)), hour: Number(values.hour) };
      }

      dateKey(date) {
        return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
      }

      isDeliveryDay(date, excluded) {
        if (excluded.has(this.dateKey(date))) return false;
        if (this.config.dateMode !== 'working') return true;
        const day = date.getUTCDay();
        if (this.config.skipSundays && day === 0) return false;
        if (this.config.skipSaturdays && day === 6) return false;
        return true;
      }

      addDeliveryDays(start, days, excluded) {
        const date = new Date(start);
        let remaining = Math.max(0, Number(days) || 0);
        while (remaining > 0) {
          date.setUTCDate(date.getUTCDate() + 1);
          if (this.isDeliveryDay(date, excluded)) remaining -= 1;
        }
        return date;
      }

      formatDate(date) {
        return new Intl.DateTimeFormat('en-IN', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short', year: this.config.showYear ? 'numeric' : undefined }).format(date);
      }

      resultHTML(template, values) {
        let html = this.escapeHTML(template);
        const strongTokens = ['pincode', 'start_date', 'end_date', 'days'];
        Object.entries(values).forEach(([key, value]) => {
          const safe = this.escapeHTML(value);
          const replacement = strongTokens.includes(key) ? `<strong>${safe}</strong>` : `<span>${safe}</span>`;
          html = html.replaceAll(`[${key}]`, replacement);
        });
        return html;
      }

      async check() {
        const pincode = String(this.input?.value || '').trim();
        if (!/^[1-9][0-9]{5}$/.test(pincode)) {
          this.requestId += 1;
          this.setLoading(false);
          this.showResult('error', this.escapeHTML(this.config.invalidFormatMessage));
          return;
        }
        const requestId = ++this.requestId;
        this.setLoading(true);
        this.showResult('loading', this.escapeHTML(this.config.loadingMessage));
        const validation = await this.validatePincode(pincode);
        if (requestId !== this.requestId || !this.isConnected) return;
        this.setLoading(false);
        if (!validation.valid) {
          this.showResult('error', this.escapeHTML(validation.apiError ? this.config.apiErrorMessage : this.config.invalidPincodeMessage));
          return;
        }
        const office = validation.offices[0] || {};
        const zone = this.findZone(pincode, validation.offices);
        const now = this.indiaNow();
        const cutoffExtra = now.hour >= Number(this.config.cutoffHour) ? Number(this.config.afterCutoffDays) || 0 : 0;
        const firstDays = (Number(zone.days) || Number(this.config.fallbackDays) || 7) + (Number(this.config.handlingDays) || 0) + cutoffExtra;
        const excluded = new Set(String(this.config.excludedDates || '').split(/[\n,;]+/).map((value) => value.trim()).filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value)));
        const startDate = this.addDeliveryDays(now.date, firstDays, excluded);
        const endDate = this.addDeliveryDays(startDate, Number(this.config.rangeExtraDays) || 0, excluded);
        const location = [office.District, office.State].filter(Boolean).join(', ');
        const html = this.resultHTML(this.config.successTemplate, {
          pincode,
          location,
          zone: zone.label || '',
          days: zone.days || this.config.fallbackDays,
          start_date: this.formatDate(startDate),
          end_date: this.formatDate(endDate)
        });
        this.showResult('success', html, zone.label || '');
      }
    });
  }
